import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { ChannelDTO } from "@ai-crm/shared";
import { Button } from "./ui/button";
import { Select } from "./ui/select";
import { useToast } from "./ui/toast";
import { Trash2 } from "lucide-react";

interface ChannelBindingPanelProps {
  funnelId: string;
  channels: ChannelDTO[];
}

export function ChannelBindingPanel({
  funnelId,
  channels,
}: ChannelBindingPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState("");

  const channelsQuery = useQuery({
    queryKey: ["wazzup-channels"],
    queryFn: api.listWazzupChannels,
  });

  const bind = useMutation({
    mutationFn: (data: { wazzupChannelId: string; type: "TELEGRAM" | "WHATSAPP" }) =>
      api.bindChannel(funnelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["funnel", funnelId] });
      queryClient.invalidateQueries({ queryKey: ["funnels"] });
      setSelectedId("");
      toast("Канал привязан", "success");
    },
    onError: (err: Error) => {
      toast(err.message, "destructive");
    },
  });

  const unbind = useMutation({
    mutationFn: (id: string) => api.unbindChannel(funnelId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["funnel", funnelId] });
      queryClient.invalidateQueries({ queryKey: ["funnels"] });
      toast("Канал отвязан", "success");
    },
    onError: (err: Error) => {
      toast(err.message, "destructive");
    },
  });

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Каналы Wazzup</h3>

      {channels.length > 0 && (
        <div className="space-y-2">
          {channels.map((ch) => (
            <div
              key={ch.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
            >
              <span>
                <span className="font-mono">{ch.wazzupChannelId}</span>
                <span className="ml-2 text-muted-foreground">{ch.type}</span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => unbind.mutate(ch.id)}
                disabled={unbind.isPending}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {channelsQuery.data && channelsQuery.data.length > 0 ? (
        <div className="flex gap-2">
          <Select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">— выберите канал —</option>
            {channelsQuery.data
              .filter(
                (c) => !channels.some((bound) => bound.wazzupChannelId === c.id),
              )
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.type}
                </option>
              ))}
          </Select>
          <Button
            disabled={!selectedId || bind.isPending}
            onClick={() => {
              const ch = channelsQuery.data?.find((c) => c.id === selectedId);
              if (ch) bind.mutate({ wazzupChannelId: ch.id, type: ch.type });
            }}
          >
            Добавить
          </Button>
        </div>
      ) : (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          Сначала подключите Wazzup.{" "}
          <Link to="/settings/wazzup" className="underline">
            Подключить
          </Link>
        </div>
      )}
    </div>
  );
}
